package top.wcpe.allincore.ui.runtime.render.model

import kotlin.jvm.JvmName

/**
 * 不可变阴影参数值对象。
 *
 * 描述一个元素的阴影绘制参数：偏移、模糊半径、颜色、扩展、内阴影标志。
 *
 * `inset=true` 表示内阴影（CSS box-shadow inset 语义）：阴影绘制在盒内、
 * 沿边缘向内衰减，而非向外投影。默认 `false`（外阴影），保持原有行为。
 *
 * ADR-0072 决策 1 镜像：真源为 `platform-client-api` 的
 * `top.wcpe.allincore.platform.client.api.render.command.ShadowParams`
 * （纯 Java 8 对外 API，按 ADR-0002 不得 KMP 化）。equals/hashCode/toString 逐位复刻
 * Lombok `@Value` 语义（Float.compare 全序比较、PRIME=59、boolean → 79/97），
 * 一致性由 jvmTest 的 RenderModelConsistencyTest 双向 roundtrip 守护。
 */
class ShadowParams(
    @get:JvmName("offsetX") val offsetX: Float,
    @get:JvmName("offsetY") val offsetY: Float,
    @get:JvmName("blur") val blur: Float,
    @get:JvmName("color") val color: Int,
    @get:JvmName("spread") val spread: Float,
    /** 是否为内阴影（inset）；true 时阴影画在盒内向内衰减。 */
    @get:JvmName("inset") val inset: Boolean,
) {

    /**
     * 兼容旧调用点的便捷构造：默认 `inset=false`（外阴影）。
     *
     * 保留 5 参构造避免所有既有调用点同步改动；需内阴影时用 6 参构造。
     */
    constructor(offsetX: Float, offsetY: Float, blur: Float, color: Int, spread: Float) :
        this(offsetX, offsetY, blur, color, spread, false)

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is ShadowParams) return false
        // 复刻 Lombok Float.compare 全序比较（NaN 等于自身、区分 ±0）
        return offsetX.compareTo(other.offsetX) == 0 &&
            offsetY.compareTo(other.offsetY) == 0 &&
            blur.compareTo(other.blur) == 0 &&
            color == other.color &&
            spread.compareTo(other.spread) == 0 &&
            inset == other.inset
    }

    // 逐位复刻 Lombok @Value：种子 1、59 倍累乘、float 用 floatToIntBits、boolean 用 79/97
    override fun hashCode(): Int = lombokHash(
        offsetX.toBits(),
        offsetY.toBits(),
        blur.toBits(),
        color,
        spread.toBits(),
        lombokBooleanHash(inset),
    )

    override fun toString(): String =
        "ShadowParams(offsetX=$offsetX, offsetY=$offsetY, blur=$blur, color=$color, spread=$spread, inset=$inset)"
}
